import {useEffect, useState} from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import BusinessIcon from '@mui/icons-material/Business';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {AffiliationInfo} from '../../backend/interfaces/affiliation-info';
import {Role} from '../../backend/interfaces/role';
import {FlatMultiTextSelect, Option} from '../flat-multi-text-select/flat-multi-text-select';
import {AlertModal} from '../alert-modal/alert-modal';
import {ContentPage} from '../content-page/content-page';
import {ErrorBox} from '../error-box/error-box';
import {LoadingContent} from '../loading-content/loading-content';
import {useAuth} from '../../contexts/auth/context';
import {useSnackBar} from '../../contexts/snack-bar/context';
import {usePaginatedTable} from '../../hooks/paginated-table';
import {translations} from '../../i18n/translations';
import {PaginatedTableData} from '../../interfaces/paginated-table-data';
import {EntitiesRepository} from '../../repositories/entities';
import {RolesRepository} from '../../repositories/roles';
import {UsersRepository} from '../../repositories/users';
import {API_PAGINATION_MAX_LIMIT} from '../../utils/constants/api';
import {ScreenPaths} from '../../enums/screen-path';

interface AffiliationRoles {
  affiliation: AffiliationInfo;
  roles: Role[];
  rolesIds: string[];
}

const fetchEntityRoles = async (entityId: string): Promise<Role[]> => {
  const roles: Role[] = [];
  let skip = 0;
  let hasMore = true;

  while (hasMore) {
    const result = await RolesRepository.instance.getRoles(
      entityId,
      API_PAGINATION_MAX_LIMIT,
      skip
    );
    roles.push(...result.data);
    skip += result.data.length;

    const totalCount = Number.isFinite(result.totalCount) ? result.totalCount : undefined;
    hasMore =
      result.data.length > 0 &&
      (totalCount === undefined
        ? result.data.length === API_PAGINATION_MAX_LIMIT
        : skip < totalCount);
  }

  return roles;
};

const areRoleIdsEqual = (first: string[], second: string[]) =>
  first.length === second.length && first.every((roleId) => second.includes(roleId));

export const UserAffiliationRolesTable = ({
  userId,
  compact = false,
}: {
  userId: string;
  compact?: boolean;
}) => {
  const {t} = useTranslation();
  const authContext = useAuth();
  const snackBarContext = useSnackBar();
  const [roleDrafts, setRoleDrafts] = useState<Record<string, string[]>>({});
  const [savingAffiliations, setSavingAffiliations] = useState<Record<string, boolean>>({});
  const [affiliationToRemove, setAffiliationToRemove] = useState<
    {entityId: string; entityName: string} | undefined
  >();
  const [isRemovingAffiliation, setIsRemovingAffiliation] = useState(false);
  const [userName, setUserName] = useState('');

  useEffect(() => {
    let isCurrent = true;

    const getUserName = async () => {
      try {
        const user = await UsersRepository.instance.getById(userId);
        if (isCurrent) {
          setUserName(user.displayName || user.name || '');
        }
      } catch (error) {
        console.debug('🔴 Could not load user name for affiliation removal confirmation.', error);
      }
    };

    if (userId) {
      getUserName();
    }

    return () => {
      isCurrent = false;
    };
  }, [userId]);

  const handleGetAffiliations = async (limit: number, skip: number) => {
    if (!userId) {
      throw new Error('Empty user id.');
    }

    const result = await UsersRepository.instance.getUserAffiliations(userId, skip, limit);
    const data: PaginatedTableData<AffiliationRoles> = await Promise.all(
      result.data.map(async (affiliationInfo) => {
        const entityId = affiliationInfo.entity.id;
        const [userAffiliation, roles] = await Promise.all([
          EntitiesRepository.instance.getAffiliation(entityId, userId),
          fetchEntityRoles(entityId),
        ]);

        return {
          id: entityId,
          item: {
            affiliation: affiliationInfo,
            roles,
            rolesIds: userAffiliation.affiliation.rolesIds ?? [],
          },
          avatarUrl: affiliationInfo.entity.picture,
          columns: [
            {columnId: 'entity-name', value: affiliationInfo.entity.name},
            {columnId: 'entity-type', value: affiliationInfo.entity.type},
          ],
        };
      })
    );

    return {data, totalCount: result.totalCount};
  };

  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    handlePageChange,
    handleApplyFilters,
    handleUpdateItem,
  } = usePaginatedTable<AffiliationRoles>({fetchData: handleGetAffiliations});

  const handleChangeRoles = (entityId: string, selected: Option | Option[]) => {
    const selectedOptions = Array.isArray(selected) ? selected : [selected];
    setRoleDrafts((previous) => ({
      ...previous,
      [entityId]: selectedOptions.map((option) => option.key),
    }));
  };

  const handleCancelChange = (entityId: string) => {
    setRoleDrafts((previous) => {
      const updated = {...previous};
      delete updated[entityId];
      return updated;
    });
  };

  const handleSaveRoles = async (entityId: string) => {
    const row = data.find((item) => item.id === entityId);
    const rolesIds = roleDrafts[entityId];
    if (!row || !rolesIds) return;

    setSavingAffiliations((previous) => ({...previous, [entityId]: true}));

    try {
      const updatedAffiliation = await EntitiesRepository.instance.updateAffiliation(
        entityId,
        userId,
        {rolesIds}
      );
      const savedRolesIds = updatedAffiliation.rolesIds ?? rolesIds;

      handleUpdateItem(entityId, [], {...row.item, rolesIds: savedRolesIds});
      handleCancelChange(entityId);
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(
          translations.role_permissions_professional_caregiver_office_screen.snackbar.update.success
        ),
      });

      if (authContext.state.session?.id === userId) {
        try {
          await authContext.refreshSessionData(
            authContext.state.session?.selectedRelationship?.id,
            authContext.state.session?.selectedAffiliation?.id
          );
        } catch (error) {
          console.debug('🔴 Could not refresh session after updating affiliation roles.', error);
        }
      }
    } catch (error) {
      console.debug('🔴 Could not update the user roles in the entity.', {
        entityId,
        userId,
        error,
      });
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(
          translations.role_permissions_professional_caregiver_office_screen.snackbar.update.error
        ),
      });
    } finally {
      setSavingAffiliations((previous) => ({...previous, [entityId]: false}));
    }
  };

  const handleAskRemoveAffiliation = (entityId: string) => {
    const row = data.find((item) => item.id === entityId);
    if (!row) return;

    setAffiliationToRemove({
      entityId,
      entityName: row.item.affiliation.entity.name,
    });
  };

  const handleRemoveAffiliation = async () => {
    if (!affiliationToRemove) return;

    const target = affiliationToRemove;
    setIsRemovingAffiliation(true);

    try {
      await UsersRepository.instance.deleteUserInEntity(target.entityId, userId);
      handleCancelChange(target.entityId);

      if (authContext.state.session?.id === userId) {
        authContext.removeAffiliation(target.entityId);
      }

      if (data.length === 1 && currentPage > 1) {
        handleApplyFilters(currentPage - 1);
      } else {
        handleApplyFilters();
      }

      console.debug('🟢 User affiliation removed.', {
        userId,
        entityId: target.entityId,
        entityName: target.entityName,
      });
      setAffiliationToRemove(undefined);
    } catch (error) {
      console.debug('🔴 Could not remove user affiliation.', {
        userId,
        entityId: target.entityId,
        entityName: target.entityName,
        error,
      });
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.user_affiliations.errors.remove_affiliation_error_message),
      });
    } finally {
      setIsRemovingAffiliation(false);
    }
  };

  const handleCloseRemoveAffiliation = () => {
    if (!isRemovingAffiliation) {
      setAffiliationToRemove(undefined);
    }
  };

  return (
    <ContentPage fullWidth={compact}>
      <Box
        sx={{
          marginTop: compact ? 0 : '16px',
          paddingX: compact ? 0 : {xs: '12px', sm: '24px'},
          paddingBottom: compact ? 0 : {xs: '32px', md: '51px'},
        }}
      >
        {isLoading ? (
          <LoadingContent marginTop="24px" sx={{minHeight: 'calc(100vh - 400px)'}} />
        ) : error ? (
          <ErrorBox error={error} />
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{backgroundColor: 'action.hover'}}>
                    <TableCell sx={{fontWeight: 700}}>
                      {t(translations.user_affiliations.entity_name_header)}
                    </TableCell>
                    <TableCell sx={{fontWeight: 700}}>
                      {t(translations.user_affiliations.entity_type_header)}
                    </TableCell>
                    <TableCell sx={{fontWeight: 700}}>
                      {t(translations.user_form.roles_ids_input)}
                    </TableCell>
                    <TableCell sx={{fontWeight: 700, minWidth: 150}}>
                      {t(translations.user_affiliations.remove_affiliation_header)}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.length > 0 ? (
                    data.map((row) => {
                      const entity = row.item.affiliation.entity;
                      const assignedRolesIds = roleDrafts[row.id] ?? row.item.rolesIds;
                      const roleOptions: Option[] = row.item.roles
                        .filter((role) => !!role.id)
                        .map((role) => ({key: role.id as string, label: role.name}));
                      const selectedRoles = roleOptions.filter((option) =>
                        assignedRolesIds.includes(option.key)
                      );
                      const hasChanges = !areRoleIdsEqual(assignedRolesIds, row.item.rolesIds);
                      const isSaving = savingAffiliations[row.id] === true;

                      return (
                        <TableRow key={row.id} hover>
                          <TableCell>
                            <Link
                              to={`${ScreenPaths.Office}${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_Entities}/${entity.id}`}
                              style={{
                                color: 'inherit',
                                textDecoration: 'none',
                                display: 'inline-block',
                              }}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1.5}
                                sx={{
                                  '&:hover .entity-affiliation-name': {
                                    textDecoration: 'underline',
                                  },
                                }}
                              >
                                <Avatar src={entity.picture} alt={entity.name}>
                                  <BusinessIcon />
                                </Avatar>
                                <Typography
                                  className="entity-affiliation-name"
                                  color="inherit"
                                  variant="body2"
                                >
                                  {entity.name}
                                </Typography>
                              </Stack>
                            </Link>
                          </TableCell>
                          <TableCell>{t(translations.entity_types[entity.type])}</TableCell>
                          <TableCell sx={{minWidth: 280, maxWidth: 280}}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.5}
                              sx={{width: '100%', maxWidth: 280}}
                            >
                              <Box sx={{flex: 1, minWidth: 0}}>
                                <FlatMultiTextSelect
                                  label={undefined}
                                  placeholder={t(
                                    translations.user_form.roles_ids_placeholder_input
                                  )}
                                  value={selectedRoles}
                                  options={roleOptions}
                                  onChange={(selected) => handleChangeRoles(row.id, selected)}
                                  disabled={isSaving || isRemovingAffiliation}
                                  sx={{width: '100%', minWidth: 0}}
                                />
                              </Box>
                              {hasChanges && (
                                <Stack direction="row" spacing={0.5} sx={{flexShrink: 0}}>
                                  <Tooltip title={t(translations.user_form.cancel_button)}>
                                    <span>
                                      <IconButton
                                        size="small"
                                        sx={{
                                          bgcolor: 'primary.main',
                                          color: 'common.white',
                                          '&:hover': {bgcolor: 'primary.dark'},
                                        }}
                                        aria-label={t(translations.user_form.cancel_button)}
                                        onClick={() => handleCancelChange(row.id)}
                                        disabled={isSaving || isRemovingAffiliation}
                                      >
                                        <CloseIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title={t(translations.user_form.save_button)}>
                                    <span>
                                      <IconButton
                                        size="small"
                                        sx={{
                                          bgcolor: 'primary.main',
                                          color: 'common.white',
                                          '&:hover': {bgcolor: 'primary.dark'},
                                        }}
                                        aria-label={t(translations.user_form.save_button)}
                                        onClick={() => handleSaveRoles(row.id)}
                                        disabled={isSaving || isRemovingAffiliation}
                                      >
                                        {isSaving ? (
                                          <CircularProgress size={18} color="inherit" />
                                        ) : (
                                          <SaveIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Stack>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleAskRemoveAffiliation(row.id)}
                              disabled={isSaving || isRemovingAffiliation}
                            >
                              {t(translations.user_affiliations.remove_affiliation_button)}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography>{t(translations.user_affiliations.no_affiliations)}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {pagesCount > 1 && (
              <Box sx={{display: 'flex', justifyContent: 'center', marginTop: '16px'}}>
                <Pagination
                  count={pagesCount}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Box>
      <AlertModal
        id="confirm-remove-user-entity-affiliation"
        opened={Boolean(affiliationToRemove)}
        title={t(translations.user_affiliations.remove_affiliation_dialog_title, {
          entity: affiliationToRemove?.entityName || '',
          user: userName,
        })}
        content={t(translations.user_affiliations.remove_affiliation_dialog_message, {
          entity: affiliationToRemove?.entityName || '',
          user: userName,
        })}
        handleClose={handleCloseRemoveAffiliation}
        preventCloseClick={isRemovingAffiliation}
        actions={[
          {
            label: t(translations.user_affiliations.remove_affiliation_dialog_action_yes_button),
            type: 'destructive',
            handleClick: () => {
              void handleRemoveAffiliation();
            },
          },
          {
            label: t(translations.user_affiliations.remove_affiliation_dialog_action_no_button),
            type: 'neutral',
            handleClick: handleCloseRemoveAffiliation,
          },
        ]}
      />
    </ContentPage>
  );
};
